/**
 * Generadores de PDF para el álbum. 3 variantes:
 *
 *   - "table-x"        → tabla con X / Xn por celda
 *   - "table-color"    → tabla con celdas resaltadas (verde tenés, ámbar repetida)
 *   - "photo"          → "foto" del álbum: cards 5x4 por país agrupadas por grupo
 *
 * Usa jsPDF directamente (sin html2canvas) para que el PDF sea vectorial,
 * imprimible y ligero (≤ 50 KB).
 */

import jsPDF from 'jspdf';
import { ALBUM_SECTIONS, isHiddenSticker, stickerCode, stickerLocalNumber } from '../albumData';
import { COUNTRY_COLOR } from '../albumDecorations';

/**
 * Mapa de código del álbum (3 letras) al emoji de bandera Unicode. Para
 * países sin bandera (FWC, CC) devolvemos null y el caller usa el cuadrado
 * de color como fallback. Para Inglaterra y Escocia usamos los emojis
 * regionales (no todos los OS los soportan).
 */
const FLAG_EMOJI = {
  ALG: '🇩🇿', ARG: '🇦🇷', AUS: '🇦🇺', AUT: '🇦🇹',
  BEL: '🇧🇪', BIH: '🇧🇦', BRA: '🇧🇷',
  CAN: '🇨🇦', CIV: '🇨🇮', COD: '🇨🇩', COL: '🇨🇴', CPV: '🇨🇻', CRO: '🇭🇷', CUW: '🇨🇼', CZE: '🇨🇿',
  ECU: '🇪🇨', EGY: '🇪🇬', ENG: '🏴\u{E0067}\u{E0062}\u{E0065}\u{E006E}\u{E0067}\u{E007F}', ESP: '🇪🇸',
  FRA: '🇫🇷', GER: '🇩🇪', GHA: '🇬🇭',
  HAI: '🇭🇹', IRN: '🇮🇷', IRQ: '🇮🇶',
  JOR: '🇯🇴', JPN: '🇯🇵', KOR: '🇰🇷', KSA: '🇸🇦',
  MAR: '🇲🇦', MEX: '🇲🇽', NED: '🇳🇱', NOR: '🇳🇴', NZL: '🇳🇿',
  PAN: '🇵🇦', PAR: '🇵🇾', POR: '🇵🇹', QAT: '🇶🇦',
  RSA: '🇿🇦', SCO: '🏴\u{E0067}\u{E0062}\u{E0073}\u{E0063}\u{E0074}\u{E007F}',
  SEN: '🇸🇳', SUI: '🇨🇭', SWE: '🇸🇪',
  TUN: '🇹🇳', TUR: '🇹🇷', URU: '🇺🇾', USA: '🇺🇸', UZB: '🇺🇿',
};

/**
 * Convierte un emoji (bandera) a un dataURL PNG dibujándolo en un canvas.
 * Cacheamos para no re-dibujar el mismo emoji muchas veces.
 */
const flagCache = new Map();
function flagImage(code) {
  if (flagCache.has(code)) return flagCache.get(code);
  const emoji = FLAG_EMOJI[code];
  if (!emoji) {
    flagCache.set(code, null);
    return null;
  }
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 48;
    const ctx = canvas.getContext('2d');
    ctx.font = '40px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillText(emoji, 32, 26);
    const url = canvas.toDataURL('image/png');
    flagCache.set(code, url);
    return url;
  } catch {
    flagCache.set(code, null);
    return null;
  }
}

// A4 portrait en mm: 210 × 297. Trabajamos en mm con jsPDF default.
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 8;

/**
 * Construye una lista plana de "rows" (FWC, cada país, CC) con sus stickers.
 * Cada row: { code, name, color, stickers: [{n, owned, reps}] }.
 */
function buildRows(stickers) {
  const byN = new Map();
  for (const s of stickers) byN.set(s.n, s);
  const rows = [];
  for (const sec of ALBUM_SECTIONS) {
    if (sec.codePrefix) {
      const list = [];
      for (let n = sec.from; n <= sec.to; n++) {
        if (isHiddenSticker(n)) continue;
        list.push(byN.get(n) || { n, owned: false, reps: 0 });
      }
      rows.push({
        code: sec.codePrefix,
        name: sec.label,
        color: '#1f2937',
        stickers: list,
        sectionId: sec.id,
      });
    }
    if (sec.countries) {
      const names = String(sec.subtitle || '').split(' · ');
      sec.countries.forEach((code, i) => {
        const list = [];
        const from = sec.from + i * 20;
        for (let j = 0; j < 20; j++) {
          const n = from + j;
          list.push(byN.get(n) || { n, owned: false, reps: 0 });
        }
        rows.push({
          code,
          name: names[i] || code,
          color: COUNTRY_COLOR[names[i]] || '#666',
          stickers: list,
          sectionId: sec.id,
        });
      });
    }
  }
  return rows;
}

/** Saca tildes/diacríticos sin cambiar la letra base. "Matías" → "Matias". */
function stripDiacritics(s) {
  return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/** "Matías Rossi" → "Matias_Rossi". Ascii-safe para filenames. */
function sanitizeNameForFile(displayName) {
  const base = stripDiacritics(displayName || 'album').trim();
  return base
    .replace(/[^a-zA-Z0-9 _-]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 50) || 'album';
}

function todayHuman() {
  const d = new Date();
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function downloadPdf(doc, displayName, suffix) {
  const safe = sanitizeNameForFile(displayName);
  doc.save(`Album_${safe}.pdf`);
}

function setFillFromHex(doc, hex) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return doc.setFillColor(200, 200, 200);
  doc.setFillColor(parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16));
}
function setDrawFromHex(doc, hex) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return doc.setDrawColor(150, 150, 150);
  doc.setDrawColor(parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16));
}
// Url del álbum — promo VATUCO en el header del PDF.
const VATUCO_URL = 'https://vatuco.com/album';

/** Header común: VATUCO link arriba a la derecha + título + subtitle.
 * `opts.displayName`: si se pasa, se renderea como byline pequeño a la
 * derecha del título: `Por Matias Rossi · 03/05/2026`.
 */
function drawTitle(doc, title, subtitle, opts = {}) {
  const { pageW = PAGE_W, withVatuco = true, displayName } = opts;

  // VATUCO link (top-right)
  if (withVatuco) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(37, 99, 235); // primary blue
    const label = 'VATUCO';
    const labelW = doc.getTextWidth(label);
    doc.textWithLink(label, pageW - MARGIN - labelW, MARGIN + 2, { url: VATUCO_URL });
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    const url = 'vatuco.com/album';
    const urlW = doc.getTextWidth(url);
    doc.textWithLink(url, pageW - MARGIN - urlW, MARGIN + 6, { url: VATUCO_URL });
  }

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(20, 20, 20);
  doc.text(title, MARGIN, MARGIN + 4);

  // Byline a la derecha del título (sin tilde)
  if (displayName) {
    const cleanName = stripDiacritics(displayName);
    const byline = `Por ${cleanName} · ${todayHuman()}`;
    const titleW = doc.getTextWidth(title);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(110, 110, 110);
    doc.text(byline, MARGIN + titleW + 6, MARGIN + 4);
  }

  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    const lines = doc.splitTextToSize(subtitle, pageW - MARGIN * 2 - 40);
    doc.text(lines.slice(0, 2), MARGIN, MARGIN + 9);
  }
}

/**
 * Dibuja la leyenda con palabras subrayadas en color:
 *   "Blanco: me falta · [LA TENGO]_verde · [REPETIDA]_amarillo"
 *
 * Devuelve la altura usada (mm) para que el caller pueda mover su top.
 */
function drawColorLegend(doc, x, y) {
  let cx = x;
  const cy = y;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  // "Blanco" + ": me falta"
  doc.setTextColor(40, 40, 40);
  doc.text('Blanco: me falta  ·  ', cx, cy);
  cx += doc.getTextWidth('Blanco: me falta  ·  ');

  // "la tengo" subrayada en verde
  const greenLabel = 'la tengo';
  const greenW = doc.getTextWidth(greenLabel);
  setFillFromHex(doc, '#bbf7d0');
  doc.rect(cx - 0.4, cy - 2.6, greenW + 0.8, 3.2, 'F');
  doc.setTextColor(22, 101, 52);
  doc.setFont('helvetica', 'bold');
  doc.text(greenLabel, cx, cy);
  cx += greenW + 1;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);
  doc.text('  ·  ', cx, cy);
  cx += doc.getTextWidth('  ·  ');

  // "repetidas" subrayada en amarillo
  const amberLabel = 'repetidas';
  const amberW = doc.getTextWidth(amberLabel);
  setFillFromHex(doc, '#fde68a');
  doc.rect(cx - 0.4, cy - 2.6, amberW + 0.8, 3.2, 'F');
  doc.setTextColor(120, 53, 15);
  doc.setFont('helvetica', 'bold');
  doc.text(amberLabel, cx, cy);
  cx += amberW + 1;

  // restablecer color/font
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);

  return cy - y; // ya estamos en y, así que el alto consumido lo maneja el caller con offset fijo.
}

/* ─────────────────── Variante 1: TABLE X ────────────────── */
/**
 * Una fila por país, 20 columnas (1..20). En cada celda:
 *   - "·" si me falta
 *   - "X" si tengo 1
 *   - "X2", "X3" si tengo repetidas (Xn)
 */
function buildTableX(stickers, displayName) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const rows = buildRows(stickers);

  drawTitle(doc, 'Mi álbum — Mundial 2026',
    'Cada celda: X = la tengo, Xn = tengo n copias, "·" = me falta. La fila izquierda es el código del país.',
    { displayName });

  const top = MARGIN + 14;
  const colCode = MARGIN;
  const codeW = 18;
  // 20 cols + columna de código => fitamos ancho útil
  const usable = PAGE_W - MARGIN * 2 - codeW;
  const cellW = usable / 20;

  // Calcular cuántas filas entran por página
  const rowH = 5.2;
  const headerH = 5.5;
  const bodyTop = top + headerH;
  const maxRowsPerPage = Math.floor((PAGE_H - bodyTop - MARGIN) / rowH);

  const drawHeader = (yTop) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(80, 80, 80);
    for (let c = 0; c < 20; c++) {
      const x = colCode + codeW + c * cellW + cellW / 2;
      doc.text(String(c + 1), x, yTop + 4, { align: 'center' });
    }
    setDrawFromHex(doc, '#cccccc');
    doc.setLineWidth(0.2);
    doc.line(MARGIN, yTop + 5, PAGE_W - MARGIN, yTop + 5);
  };

  drawHeader(top);

  // y = top de la fila.
  let y = bodyTop;
  rows.forEach((row, i) => {
    if (i > 0 && i % maxRowsPerPage === 0) {
      doc.addPage();
      drawTitle(doc, 'Mi álbum — Mundial 2026', null, { displayName });
      drawHeader(MARGIN + 14);
      y = MARGIN + 14 + headerH;
    }
    const cellH = rowH - 0.5;
    const cy = y + cellH / 2;
    // Banderita + código
    setFillFromHex(doc, row.color);
    doc.rect(colCode, y + (cellH - 3.2) / 2, 3.6, 3.2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(30, 30, 30);
    doc.text(row.code, colCode + 4.5, cy + 1.2);

    doc.setLineWidth(0.1);
    setDrawFromHex(doc, '#e5e5e5');
    for (let c = 0; c < 20; c++) {
      const x = colCode + codeW + c * cellW;
      const sticker = row.stickers[c];
      doc.rect(x, y, cellW, cellH);
      if (sticker?.owned) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        const reps = sticker.reps || 0;
        const txt = reps >= 2 ? `X${reps}` : 'X';
        doc.setTextColor(20, 100, 30);
        doc.text(txt, x + cellW / 2, cy + 1.2, { align: 'center' });
      } else {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(180, 180, 180);
        doc.text('·', x + cellW / 2, cy + 1, { align: 'center' });
      }
    }
    y += rowH;
  });

  return doc;
}

/* ─────────────────── Variante 2: TABLE COLOR ────────────── */
/**
 * Misma grilla, pero ahora la celda muestra el NÚMERO (1..20) y se
 * resalta con color: verde si la tengo, ámbar si tengo repetida. Las
 * faltantes quedan en blanco / gris muy claro.
 */
function buildTableColor(stickers, displayName) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const rows = buildRows(stickers);

  drawTitle(doc, 'Mi álbum — Mundial 2026', null, { displayName });
  drawColorLegend(doc, MARGIN, MARGIN + 11);

  const top = MARGIN + 14;
  const colCode = MARGIN;
  const codeW = 18;
  const usable = PAGE_W - MARGIN * 2 - codeW;
  const cellW = usable / 20;
  const rowH = 5.2;
  const headerH = 5.5;
  const bodyTop = top + headerH;
  const maxRowsPerPage = Math.floor((PAGE_H - bodyTop - MARGIN) / rowH);

  const drawHeader = (yTop) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(80, 80, 80);
    for (let c = 0; c < 20; c++) {
      const x = colCode + codeW + c * cellW + cellW / 2;
      doc.text(String(c + 1), x, yTop + 4, { align: 'center' });
    }
    setDrawFromHex(doc, '#cccccc');
    doc.setLineWidth(0.2);
    doc.line(MARGIN, yTop + 5, PAGE_W - MARGIN, yTop + 5);
  };

  drawHeader(top);

  // y = top de la fila.
  let y = bodyTop;
  rows.forEach((row, i) => {
    if (i > 0 && i % maxRowsPerPage === 0) {
      doc.addPage();
      drawTitle(doc, 'Mi álbum — Mundial 2026', null, { displayName });
      drawHeader(MARGIN + 14);
      y = MARGIN + 14 + headerH;
    }
    const cellH = rowH - 0.5;
    const cy = y + cellH / 2;
    setFillFromHex(doc, row.color);
    doc.rect(colCode, y + (cellH - 3.2) / 2, 3.6, 3.2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(30, 30, 30);
    doc.text(row.code, colCode + 4.5, cy + 1.2);

    doc.setLineWidth(0.1);
    for (let c = 0; c < 20; c++) {
      const x = colCode + codeW + c * cellW;
      const sticker = row.stickers[c];
      const reps = sticker?.reps || 0;
      const owned = !!sticker?.owned;
      // fill
      if (owned && reps >= 2) {
        setFillFromHex(doc, '#fde68a'); // ámbar
        doc.rect(x, y, cellW, cellH, 'F');
      } else if (owned) {
        setFillFromHex(doc, '#bbf7d0'); // verde claro
        doc.rect(x, y, cellW, cellH, 'F');
      }
      // border
      setDrawFromHex(doc, '#d1d5db');
      doc.rect(x, y, cellW, cellH);
      // Número principal centrado vertical
      doc.setFont('helvetica', owned ? 'bold' : 'normal');
      doc.setFontSize(7);
      if (owned) doc.setTextColor(22, 101, 52);
      else doc.setTextColor(180, 180, 180);
      const cellLabel = sticker ? String(stickerLocalNumber(sticker.n)) : String(c + 1);
      doc.text(cellLabel, x + cellW / 2, cy + 1.2, { align: 'center' });

      // Badge "x{n}" arriba a la derecha en ámbar (sólo si reps >= 2)
      if (owned && reps >= 2) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(4.5);
        doc.setTextColor(120, 53, 15);
        doc.text(`x${reps}`, x + cellW - 0.5, y + 1.6, { align: 'right' });
      }
    }
    y += rowH;
  });

  return doc;
}

/* ───────── Variante 2b: TABLE COLOR + CÓDIGO COMPLETO ──── */
/**
 * Igual que la variante "color" pero cada celda muestra el código completo
 * (`ARG3`, `BEL12`, etc) en lugar del número 1..20. Banderitas reales
 * (emoji renderizado en canvas) en la izquierda. Filas un poco más anchas
 * para aprovechar el espacio.
 */
function buildTableColorCode(stickers, displayName) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const rows = buildRows(stickers);

  drawTitle(doc, 'Mi álbum — Mundial 2026', null, { displayName });
  drawColorLegend(doc, MARGIN, MARGIN + 11);

  const top = MARGIN + 14;
  // Banderitas más chicas + filas más cortas para que entre todo en 1 página.
  const flagW = 3.6;
  const flagH = 2.6;
  const codeW = 11.5;
  const usable = PAGE_W - MARGIN * 2 - codeW;
  const cellW = usable / 20;
  const rowH = 5.0;
  const headerH = 5.5;
  const bodyTop = top + headerH;
  const maxRowsPerPage = Math.floor((PAGE_H - bodyTop - MARGIN) / rowH);

  const drawHeader = (yTop) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(80, 80, 80);
    for (let c = 0; c < 20; c++) {
      const x = MARGIN + codeW + c * cellW + cellW / 2;
      doc.text(String(c + 1), x, yTop + 3.5, { align: 'center' });
    }
    setDrawFromHex(doc, '#cccccc');
    doc.setLineWidth(0.2);
    doc.line(MARGIN, yTop + 4.8, PAGE_W - MARGIN, yTop + 4.8);
  };

  drawHeader(top);

  // y = top de la fila (fix solapamiento con header).
  let y = bodyTop;
  rows.forEach((row, i) => {
    if (i > 0 && i % maxRowsPerPage === 0) {
      doc.addPage();
      drawTitle(doc, 'Mi álbum — Mundial 2026', null, { displayName });
      drawColorLegend(doc, MARGIN, MARGIN + 11);
      drawHeader(MARGIN + 14);
      y = MARGIN + 14 + headerH;
    }
    const cellH = rowH - 0.5;
    const cy = y + cellH / 2; // centro vertical de la fila

    // Banderita o cuadrado de color (FWC/CC) centradas verticalmente.
    const flag = flagImage(row.code);
    const flagY = y + (cellH - flagH) / 2;
    if (flag) {
      try {
        doc.addImage(flag, 'PNG', MARGIN, flagY, flagW, flagH);
      } catch {
        setFillFromHex(doc, row.color);
        doc.rect(MARGIN, flagY, flagW, flagH, 'F');
      }
    } else {
      setFillFromHex(doc, row.color);
      doc.rect(MARGIN, flagY, flagW, flagH, 'F');
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(30, 30, 30);
    doc.text(row.code, MARGIN + flagW + 0.8, cy + 1);

    doc.setLineWidth(0.1);
    for (let c = 0; c < 20; c++) {
      const x = MARGIN + codeW + c * cellW;
      const sticker = row.stickers[c];
      const reps = sticker?.reps || 0;
      const owned = !!sticker?.owned;
      // fill
      if (owned && reps >= 2) {
        setFillFromHex(doc, '#fde68a');
        doc.rect(x, y, cellW, cellH, 'F');
      } else if (owned) {
        setFillFromHex(doc, '#bbf7d0');
        doc.rect(x, y, cellW, cellH, 'F');
      }
      // border
      setDrawFromHex(doc, '#d1d5db');
      doc.rect(x, y, cellW, cellH);
      // código completo (ARG3, BEL12...)
      doc.setFont('helvetica', owned ? 'bold' : 'normal');
      doc.setFontSize(5.8);
      if (owned) doc.setTextColor(22, 101, 52);
      else doc.setTextColor(180, 180, 180);
      const code = sticker ? stickerCode(sticker.n) : String(c + 1);
      doc.text(code, x + cellW / 2, cy + 1, { align: 'center' });
      // Badge "x{n}" arriba a la derecha (ámbar) si repetida
      if (owned && reps >= 2) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(4.2);
        doc.setTextColor(120, 53, 15);
        doc.text(`x${reps}`, x + cellW - 0.4, y + 1.6, { align: 'right' });
      }
    }
    y += rowH;
  });

  return doc;
}

/* ─────────────────── Variante 3: PHOTO (cards) ──────────── */
/**
 * "Foto" del álbum: 8 cards por fila (FWC + grupos A-L con sus 4 países).
 * Cada card es 5×4 figuritas. Con 8 cols entra en 1 sola página A4.
 *
 * Layout:
 *   Fila 0: FWC (sola, ancha) + CC (al lado).
 *   Filas 1-6: Grupos A-L, cada par de filas = 2 grupos × 4 países = 8 cards.
 */
function buildPhoto(stickers, displayName) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const PAGE_W_L = 297;
  const M = 6;
  const rows = buildRows(stickers);
  const fwcRow = rows.find(r => r.sectionId === 'FWC');
  const ccRow = rows.find(r => r.sectionId === 'CC');
  const groupRows = rows.filter(r => r.sectionId !== 'FWC' && r.sectionId !== 'CC');

  // Bandejas
  const cols = 8;
  const cardGapX = 1.5;
  const cardGapY = 1.4;
  const innerW = PAGE_W_L - M * 2;
  const cardW = (innerW - cardGapX * (cols - 1)) / cols;
  // Achicamos el alto de cada celda y el header para que las 7 filas
  // (header + 6 grupos) entren cómodas en una página A4 landscape.
  const cellW = (cardW - 1) / 5;
  const cellH = cellW * 0.72;
  const headerH = 3.4;
  const cardH = headerH + cellH * 4 + 0.6;

  const drawCard = (row, x, y, w, h) => {
    // border
    setDrawFromHex(doc, '#cbd5e1');
    doc.setLineWidth(0.2);
    doc.rect(x, y, w, h);
    // header strip with country color
    setFillFromHex(doc, row.color);
    doc.rect(x, y, w, headerH, 'F');
    // code label
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6);
    doc.setTextColor(255, 255, 255);
    doc.text(row.code, x + 1.2, y + 2.4);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(4.6);
    const nameMaxW = w - 7;
    const lines = doc.splitTextToSize(row.name, nameMaxW);
    doc.text(lines[0] || '', x + w - 1.2, y + 2.4, { align: 'right' });

    // grid 5 × 4 (20 stickers para países y FWC, 14 para CC — los que sobran quedan blancos)
    const gridTop = y + headerH + 0.5;
    for (let r = 0; r < 4; r++) {
      for (let c = 0; c < 5; c++) {
        const idx = r * 5 + c;
        const sticker = row.stickers[idx];
        const cx = x + 0.5 + c * cellW;
        const cy = gridTop + r * cellH;
        if (sticker) {
          const owned = !!sticker.owned;
          const reps = sticker.reps || 0;
          if (owned && reps >= 2) {
            setFillFromHex(doc, '#fde68a');
            doc.rect(cx, cy, cellW - 0.3, cellH - 0.3, 'F');
          } else if (owned) {
            setFillFromHex(doc, '#bbf7d0');
            doc.rect(cx, cy, cellW - 0.3, cellH - 0.3, 'F');
          }
          setDrawFromHex(doc, '#d1d5db');
          doc.setLineWidth(0.08);
          doc.rect(cx, cy, cellW - 0.3, cellH - 0.3);

          // Número centrado: verde si owned (cualquier estado), gris si falta.
          doc.setFont('helvetica', owned ? 'bold' : 'normal');
          doc.setFontSize(5);
          if (owned) doc.setTextColor(22, 101, 52);
          else doc.setTextColor(160, 160, 160);
          doc.text(String(stickerLocalNumber(sticker.n)), cx + (cellW - 0.3) / 2, cy + cellH / 2 + 0.5, { align: 'center' });

          // Badge "x{n}" arriba a la derecha si repetida
          if (owned && reps >= 2) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(3.6);
            doc.setTextColor(120, 53, 15);
            doc.text(`x${reps}`, cx + (cellW - 0.3) - 0.3, cy + 1.6, { align: 'right' });
          }
        }
      }
    }
  };

  // Fila 0: izquierda (3 cols) = título + leyenda; centro (3 cols) =
  // VATUCO + url; derecha (cols 7 y 8) = FWC y CC.
  const y0 = M;
  const leftBlockW = cardW * 3 + cardGapX * 2;
  const centerX = M + leftBlockW + cardGapX;
  const xFwc = M + cardW * 6 + cardGapX * 6;
  const xCc = xFwc + cardW + cardGapX;

  // Izquierda: título + byline + leyenda
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(20, 20, 20);
  doc.text('Mi álbum — Mundial 2026', M, y0 + 8);
  if (displayName) {
    const cleanName = stripDiacritics(displayName);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 110);
    doc.text(`Por ${cleanName} · ${todayHuman()}`, M, y0 + 14);
  }
  drawColorLegend(doc, M, y0 + 20);

  // Centro: VATUCO + url, centrado vertical y horizontalmente en la columna.
  const centerW = cardW * 3 + cardGapX * 2;
  const centerYMid = y0 + cardH / 2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(37, 99, 235);
  const vatucoLabel = 'VATUCO';
  const vatucoW = doc.getTextWidth(vatucoLabel);
  doc.textWithLink(
    vatucoLabel,
    centerX + centerW / 2 - vatucoW / 2,
    centerYMid + 1,
    { url: VATUCO_URL }
  );
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  const urlLabel = 'www.vatuco.com';
  const urlW = doc.getTextWidth(urlLabel);
  doc.textWithLink(
    urlLabel,
    centerX + centerW / 2 - urlW / 2,
    centerYMid + 6,
    { url: VATUCO_URL }
  );

  if (fwcRow) drawCard(fwcRow, xFwc, y0, cardW, cardH);
  if (ccRow) drawCard(ccRow, xCc, y0, cardW, cardH);

  // Filas 1+: 8 cards por fila (= 2 grupos × 4 países)
  let col = 0;
  let yCur = y0 + cardH + cardGapY;
  for (let i = 0; i < groupRows.length; i++) {
    const r = groupRows[i];
    const xCur = M + col * (cardW + cardGapX);
    drawCard(r, xCur, yCur, cardW, cardH);
    col++;
    if (col >= cols) {
      col = 0;
      yCur += cardH + cardGapY;
    }
  }

  return doc;
}

/* ─────────────────── Public API ─────────────────────────── */

export const PDF_VARIANTS = ['table-x', 'table-color', 'table-color-code', 'photo'];

export function exportPdf(variant, stickers, displayName) {
  let doc;
  if (variant === 'table-x') doc = buildTableX(stickers, displayName);
  else if (variant === 'table-color') doc = buildTableColor(stickers, displayName);
  else if (variant === 'table-color-code') doc = buildTableColorCode(stickers, displayName);
  else if (variant === 'photo') doc = buildPhoto(stickers, displayName);
  else throw new Error('Unknown PDF variant: ' + variant);
  downloadPdf(doc, displayName, variant);
}
